// SMSF State Context - Centralized state management for SMSF Calculator
// Replaces 50+ useState hooks with a single reducer pattern
// Includes LocalStorage persistence for data retention

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';

// Action Types
export const SMSF_ACTIONS = {
    // Fund Data
    SET_SMSF_DATA: 'SET_SMSF_DATA',
    UPDATE_SMSF_FIELD: 'UPDATE_SMSF_FIELD',
    
    // Member Profile
    SET_MEMBER_PROFILE: 'SET_MEMBER_PROFILE',
    UPDATE_MEMBER_FIELD: 'UPDATE_MEMBER_FIELD',
    
    // Constraints
    SET_CONSTRAINTS: 'SET_CONSTRAINTS',
    UPDATE_CONSTRAINT: 'UPDATE_CONSTRAINT',
    
    // Fees
    SET_FEE: 'SET_FEE',
    SET_ALL_FEES: 'SET_ALL_FEES',
    
    // Contributions
    SET_CONTRIBUTION: 'SET_CONTRIBUTION',
    SET_ALL_CONTRIBUTIONS: 'SET_ALL_CONTRIBUTIONS',
    
    // Property/LRBA
    SET_PROPERTY_FIELD: 'SET_PROPERTY_FIELD',
    TOGGLE_DIRECT_PROPERTY: 'TOGGLE_DIRECT_PROPERTY',
    
    // Asset Allocation
    SET_ASSET_ALLOCATION_TARGET: 'SET_ASSET_ALLOCATION_TARGET',
    SET_ASSET_ALLOCATION_ACTUAL: 'SET_ASSET_ALLOCATION_ACTUAL',
    SET_ASSET_VALUE: 'SET_ASSET_VALUE',
    SET_ASSET_PERFORMANCE: 'SET_ASSET_PERFORMANCE',
    
    // Succession/Beneficiaries
    SET_BENEFICIARIES: 'SET_BENEFICIARIES',
    ADD_BENEFICIARY: 'ADD_BENEFICIARY',
    REMOVE_BENEFICIARY: 'REMOVE_BENEFICIARY',
    UPDATE_BENEFICIARY: 'UPDATE_BENEFICIARY',
    SET_DEATH_BENEFIT_NOMINATIONS: 'SET_DEATH_BENEFIT_NOMINATIONS',
    
    // Pension
    TOGGLE_REVERSIONARY_PENSION: 'TOGGLE_REVERSIONARY_PENSION',
    SET_REVERSIONARY_AMOUNT: 'SET_REVERSIONARY_AMOUNT',
    SET_SPOUSE_AGE: 'SET_SPOUSE_AGE',
    
    // Tax Components
    SET_TAX_COMPONENT: 'SET_TAX_COMPONENT',
    
    // UI State
    SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
    TOGGLE_TUTORIAL: 'TOGGLE_TUTORIAL',
    
    // Bulk Operations
    RESET_STATE: 'RESET_STATE',
    LOAD_STATE: 'LOAD_STATE',
    MERGE_STATE: 'MERGE_STATE'
};

// Initial State - FULLY POPULATED WITH COMPREHENSIVE DEMO DATA FOR PDF TESTING
const initialState = {
    // Active UI state
    activeTab: 'dashboard',
    showTutorial: false,
    
    // Fund data - COMPREHENSIVE
    smsfData: {
        fundName: 'Smith Family Superannuation Fund',
        abn: '12 345 678 901',
        tfn: '123 456 789',
        balance: 1200000,
        totalAssets: 1200000,
        trusteeType: 'individual',
        numberOfMembers: 3,
        hasLRBA: true,
        hasCollectibles: true,
        hasInHouseAssets: true,
        hasDirectProperty: true,
        hasCommercialProperty: false,
        members: [
            { 
                name: 'John Smith', 
                age: 58, 
                balance: 700000, 
                tbr: 450000,
                retirementAge: 67,
                concessionalUsed: 12000,
                nonConcessionalUsed: 50000,
                insuranceCover: 500000,
                pensionPhase: false
            },
            { 
                name: 'Mary Smith', 
                age: 56, 
                balance: 400000, 
                tbr: 0,
                retirementAge: 67,
                concessionalUsed: 6500,
                nonConcessionalUsed: 35000,
                insuranceCover: 300000,
                pensionPhase: false
            },
            { 
                name: 'Sarah Smith', 
                age: 28, 
                balance: 100000, 
                tbr: 0,
                retirementAge: 67,
                concessionalUsed: 0,
                nonConcessionalUsed: 0,
                insuranceCover: 200000,
                pensionPhase: false
            }
        ],
        records: {}
    },
    
    // Member profile (age removed - use individual member ages instead)
    memberProfile: {
        riskTolerance: 'MEDIUM',
        timeHorizon: 20,
        investmentGoals: ['growth', 'income', 'retirement-income']
    },
    
    // Constraints
    constraints: {
        maxRealEstate: 0.6,
        maxCollectibles: 0.2,
        maxAlternativeInvestments: 0.25
    },
    
    // Fund administration - COMPREHENSIVE
    smsfTrusteeCount: 3,
    smsfEstablishedDate: '2018-03-15',
    
    // Fees - COMPREHENSIVE
    fees: {
        audit: 2200,
        admin: 3500,
        accounting: 4200
    },
    smsfAuditFees: 2200,
    smsfAdminFees: 3500,
    smsfAccountingFees: 4200,
    
    // Contributions - COMPREHENSIVE WITH USAGE
    contributions: {
        concessionalUsed: 18500,
        nonConcessionalUsed: 85000,
        yourTbcUsed: 450000,
        partnerTbcUsed: 0
    },
    smsfConcessionalUsed: 18500,
    smsfNonConcessionalUsed: 85000,
    
    // Property/LRBA - COMPREHENSIVE WITH FULL DETAILS
    property: {
        hasDirectProperty: true,
        propertyValue: 850000,
        propertyLoan: 425000,
        propertyInterestRate: 6.25,
        propertyLoanTerm: 20,
        propertyRentalYield: 4.8,
        inHouseAssets: 95000,
        relatedPartyLoans: 0,
        hasCommercialProperty: false
    },
    smsfDirectProperty: true,
    smsfPropertyValue: 850000,
    smsfPropertyLoan: 425000,
    smsfPropertyInterestRate: 6.25,
    smsfPropertyLoanTerm: 20,
    smsfPropertyRentalYield: 4.8,
    smsfInHouseAssets: 95000,
    smsfRelatedPartyLoans: 0,
    
    // Collectibles - COMPREHENSIVE
    collectibles: {
        hasCollectibles: true,
        items: [
            { type: 'Vintage Vehicles', description: '1969 Ford Mustang', value: 120000, valuationDate: '2024-06-30', insured: true },
            { type: 'Artwork', description: 'Australian Contemporary Art Collection', value: 85000, valuationDate: '2024-08-15', insured: true },
            { type: 'Wine Collection', description: 'Premium Australian Wines', value: 45000, valuationDate: '2024-05-20', insured: true }
        ]
    },
    
    // Asset allocation
    assetAllocation: {
        targets: {
            australian_shares: 25,
            international_shares_ex_us: 20,
            us_shares: 10,
            property: 20,
            fixed_income: 20,
            alternatives: 5
        },
        actual: {
            australian_shares: 0,
            international_shares_ex_us: 0,
            us_shares: 0,
            property: 0,
            fixed_income: 0,
            alternatives: 0
        },
        values: {
            australian_shares: 0,
            international_shares_ex_us: 0,
            us_shares: 0,
            property: 0,
            fixed_income: 0,
            alternatives: 0
        },
        performance: {
            australian_shares: 0,
            international_shares_ex_us: 0,
            us_shares: 0,
            property: 0,
            fixed_income: 0,
            alternatives: 0
        }
    },
    
    // Succession planning - COMPREHENSIVE WITH BENEFICIARIES
    succession: {
        deathBenefitNominations: true,
        reversionaryPensionEnabled: true,
        reversionaryPensionAmount: 65000,
        spouseAge: 56,
        beneficiaries: [
            { name: 'Mary Smith', age: 56, relationship: 'spouse', isDependent: true, percentage: 60 },
            { name: 'Sarah Smith', age: 28, relationship: 'daughter', isDependent: false, percentage: 30 },
            { name: 'James Smith', age: 25, relationship: 'son', isDependent: false, percentage: 10 }
        ],
        taxFreeComponent: 650000,
        taxableComponent: 450000
    },
    deathBenefitNominations: true,
    beneficiaries: [
        { name: 'Mary Smith', age: 56, relationship: 'spouse', isDependent: true, percentage: 60 },
        { name: 'Sarah Smith', age: 28, relationship: 'daughter', isDependent: false, percentage: 30 },
        { name: 'James Smith', age: 25, relationship: 'son', isDependent: false, percentage: 10 }
    ],
    reversionaryPensionEnabled: true,
    reversionaryPensionAmount: 65000,
    taxFreeComponent: 650000,
    taxableComponent: 450000
};

// Reducer function
const smsfReducer = (state, action) => {
    switch (action.type) {
        // Fund Data Actions
        case SMSF_ACTIONS.SET_SMSF_DATA:
            return {
                ...state,
                smsfData: { ...state.smsfData, ...action.payload }
            };
            
        case SMSF_ACTIONS.UPDATE_SMSF_FIELD:
            return {
                ...state,
                smsfData: {
                    ...state.smsfData,
                    [action.payload.field]: action.payload.value
                }
            };
        
        // Member Profile Actions
        case SMSF_ACTIONS.SET_MEMBER_PROFILE:
            return {
                ...state,
                memberProfile: { ...state.memberProfile, ...action.payload }
            };
            
        case SMSF_ACTIONS.UPDATE_MEMBER_FIELD:
            return {
                ...state,
                memberProfile: {
                    ...state.memberProfile,
                    [action.payload.field]: action.payload.value
                }
            };
        
        // Constraints Actions
        case SMSF_ACTIONS.SET_CONSTRAINTS:
            return {
                ...state,
                constraints: { ...state.constraints, ...action.payload }
            };
            
        case SMSF_ACTIONS.UPDATE_CONSTRAINT:
            return {
                ...state,
                constraints: {
                    ...state.constraints,
                    [action.payload.field]: action.payload.value
                }
            };
        
        // Fee Actions
        case SMSF_ACTIONS.SET_FEE:
            return {
                ...state,
                fees: {
                    ...state.fees,
                    [action.payload.type]: action.payload.value
                }
            };
            
        case SMSF_ACTIONS.SET_ALL_FEES:
            return {
                ...state,
                fees: { ...state.fees, ...action.payload }
            };
        
        // Contribution Actions
        case SMSF_ACTIONS.SET_CONTRIBUTION:
            return {
                ...state,
                contributions: {
                    ...state.contributions,
                    [action.payload.type]: action.payload.value
                }
            };
            
        case SMSF_ACTIONS.SET_ALL_CONTRIBUTIONS:
            return {
                ...state,
                contributions: { ...state.contributions, ...action.payload }
            };
        
        // Property Actions
        case SMSF_ACTIONS.SET_PROPERTY_FIELD:
            return {
                ...state,
                property: {
                    ...state.property,
                    [action.payload.field]: action.payload.value
                }
            };
            
        case SMSF_ACTIONS.TOGGLE_DIRECT_PROPERTY:
            return {
                ...state,
                property: {
                    ...state.property,
                    hasDirectProperty: !state.property.hasDirectProperty
                }
            };
        
        // Asset Allocation Actions
        case SMSF_ACTIONS.SET_ASSET_ALLOCATION_TARGET:
            return {
                ...state,
                assetAllocation: {
                    ...state.assetAllocation,
                    targets: {
                        ...state.assetAllocation.targets,
                        [action.payload.asset]: action.payload.value
                    }
                }
            };
            
        case SMSF_ACTIONS.SET_ASSET_ALLOCATION_ACTUAL:
            return {
                ...state,
                assetAllocation: {
                    ...state.assetAllocation,
                    actual: {
                        ...state.assetAllocation.actual,
                        [action.payload.asset]: action.payload.value
                    }
                }
            };
            
        case SMSF_ACTIONS.SET_ASSET_VALUE:
            return {
                ...state,
                assetAllocation: {
                    ...state.assetAllocation,
                    values: {
                        ...state.assetAllocation.values,
                        [action.payload.asset]: action.payload.value
                    }
                }
            };
            
        case SMSF_ACTIONS.SET_ASSET_PERFORMANCE:
            return {
                ...state,
                assetAllocation: {
                    ...state.assetAllocation,
                    performance: {
                        ...state.assetAllocation.performance,
                        [action.payload.asset]: action.payload.value
                    }
                }
            };
        
        // Succession Actions
        case SMSF_ACTIONS.SET_BENEFICIARIES:
            return {
                ...state,
                succession: {
                    ...state.succession,
                    beneficiaries: action.payload
                }
            };
            
        case SMSF_ACTIONS.ADD_BENEFICIARY:
            return {
                ...state,
                succession: {
                    ...state.succession,
                    beneficiaries: [...state.succession.beneficiaries, action.payload]
                }
            };
            
        case SMSF_ACTIONS.REMOVE_BENEFICIARY:
            return {
                ...state,
                succession: {
                    ...state.succession,
                    beneficiaries: state.succession.beneficiaries.filter((_, index) => index !== action.payload)
                }
            };
            
        case SMSF_ACTIONS.UPDATE_BENEFICIARY:
            return {
                ...state,
                succession: {
                    ...state.succession,
                    beneficiaries: state.succession.beneficiaries.map((ben, index) =>
                        index === action.payload.index ? { ...ben, ...action.payload.data } : ben
                    )
                }
            };
            
        case SMSF_ACTIONS.SET_DEATH_BENEFIT_NOMINATIONS:
            return {
                ...state,
                succession: {
                    ...state.succession,
                    deathBenefitNominations: action.payload
                }
            };
        
        // Pension Actions
        case SMSF_ACTIONS.TOGGLE_REVERSIONARY_PENSION:
            return {
                ...state,
                succession: {
                    ...state.succession,
                    reversionaryPensionEnabled: !state.succession.reversionaryPensionEnabled
                }
            };
            
        case SMSF_ACTIONS.SET_REVERSIONARY_AMOUNT:
            return {
                ...state,
                succession: {
                    ...state.succession,
                    reversionaryPensionAmount: action.payload
                }
            };
            
        case SMSF_ACTIONS.SET_SPOUSE_AGE:
            return {
                ...state,
                succession: {
                    ...state.succession,
                    spouseAge: action.payload
                }
            };
        
        // Tax Component Actions
        case SMSF_ACTIONS.SET_TAX_COMPONENT:
            return {
                ...state,
                succession: {
                    ...state.succession,
                    [action.payload.component]: action.payload.value
                }
            };
        
        // UI Actions
        case SMSF_ACTIONS.SET_ACTIVE_TAB:
            return {
                ...state,
                activeTab: action.payload
            };
            
        case SMSF_ACTIONS.TOGGLE_TUTORIAL:
            return {
                ...state,
                showTutorial: !state.showTutorial
            };
        
        // Bulk Operations
        case SMSF_ACTIONS.RESET_STATE:
            return initialState;
            
        case SMSF_ACTIONS.LOAD_STATE:
            return {
                ...initialState,
                ...action.payload
            };
            
        case SMSF_ACTIONS.MERGE_STATE:
            return {
                ...state,
                ...action.payload
            };
        
        // Single field updates (backwards compatibility)
        case 'SET_TRUSTEE_COUNT':
            return { ...state, smsfTrusteeCount: action.payload };
        case 'SET_ESTABLISHED_DATE':
            return { ...state, smsfEstablishedDate: action.payload };
        
        default:
            return state;
    }
};

// Create Context
const SMSFStateContext = createContext();
const SMSFDispatchContext = createContext();

// LocalStorage key
const STORAGE_KEY = 'smsf_calculator_state';

// Load state from LocalStorage
const loadStateFromStorage = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            // Validate it's not corrupted
            if (parsed && typeof parsed === 'object') {
                return parsed;
            }
        }
    } catch (error) {
        console.error('Failed to load SMSF state from localStorage:', error);
    }
    return null;
};

// Save state to LocalStorage
const saveStateToStorage = (state) => {
    try {
        // Don't save activeTab or showTutorial (UI state only)
        const {activeTab, showTutorial, ...dataToSave} = state;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
        console.error('Failed to save SMSF state to localStorage:', error);
    }
};

// Clear state from LocalStorage
export const clearSMSFStorage = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
        return true;
    } catch (error) {
        console.error('Failed to clear SMSF state from localStorage:', error);
        return false;
    }
};

// Provider Component
export const SMSFStateProvider = ({ children, initialData = {} }) => {
    // Try to load from localStorage first, then merge with initialData
    const savedState = loadStateFromStorage();
    const mergedInitialState = savedState 
        ? { ...initialState, ...savedState, ...initialData }
        : { ...initialState, ...initialData };

    const [state, dispatch] = useReducer(
        smsfReducer,
        mergedInitialState
    );

    // Auto-save to localStorage whenever state changes
    useEffect(() => {
        saveStateToStorage(state);
    }, [state]);
    
    return (
        <SMSFStateContext.Provider value={state}>
            <SMSFDispatchContext.Provider value={dispatch}>
                {children}
            </SMSFDispatchContext.Provider>
        </SMSFStateContext.Provider>
    );
};

// Custom hooks for using context
export const useSMSFState = () => {
    const context = useContext(SMSFStateContext);
    if (context === undefined) {
        throw new Error('useSMSFState must be used within SMSFStateProvider');
    }
    return context;
};

export const useSMSFDispatch = () => {
    const context = useContext(SMSFDispatchContext);
    if (context === undefined) {
        throw new Error('useSMSFDispatch must be used within SMSFStateProvider');
    }
    return context;
};

// Combined hook for convenience
export const useSMSF = () => {
    return [useSMSFState(), useSMSFDispatch()];
};

// Action creators for common operations
export const useSMSFActions = () => {
    const dispatch = useSMSFDispatch();
    
    return {
        // Fund data
        updateSMSFData: useCallback((data) => {
            dispatch({ type: SMSF_ACTIONS.SET_SMSF_DATA, payload: data });
        }, [dispatch]),
        
        updateSMSFField: useCallback((field, value) => {
            dispatch({ type: SMSF_ACTIONS.UPDATE_SMSF_FIELD, payload: { field, value } });
        }, [dispatch]),
        
        // Member profile
        updateMemberProfile: useCallback((data) => {
            dispatch({ type: SMSF_ACTIONS.SET_MEMBER_PROFILE, payload: data });
        }, [dispatch]),
        
        updateMemberField: useCallback((field, value) => {
            dispatch({ type: SMSF_ACTIONS.UPDATE_MEMBER_FIELD, payload: { field, value } });
        }, [dispatch]),
        
        // Fees
        setFee: useCallback((type, value) => {
            dispatch({ type: SMSF_ACTIONS.SET_FEE, payload: { type, value } });
        }, [dispatch]),
        
        setAllFees: useCallback((fees) => {
            dispatch({ type: SMSF_ACTIONS.SET_ALL_FEES, payload: fees });
        }, [dispatch]),
        
        // Contributions
        setContribution: useCallback((type, value) => {
            dispatch({ type: SMSF_ACTIONS.SET_CONTRIBUTION, payload: { type, value } });
        }, [dispatch]),
        
        setAllContributions: useCallback((contributions) => {
            dispatch({ type: SMSF_ACTIONS.SET_ALL_CONTRIBUTIONS, payload: contributions });
        }, [dispatch]),
        
        // Property
        setPropertyField: useCallback((field, value) => {
            dispatch({ type: SMSF_ACTIONS.SET_PROPERTY_FIELD, payload: { field, value } });
        }, [dispatch]),
        
        toggleDirectProperty: useCallback(() => {
            dispatch({ type: SMSF_ACTIONS.TOGGLE_DIRECT_PROPERTY });
        }, [dispatch]),
        
        // Asset allocation
        setAssetTarget: useCallback((asset, value) => {
            dispatch({ type: SMSF_ACTIONS.SET_ASSET_ALLOCATION_TARGET, payload: { asset, value } });
        }, [dispatch]),
        
        setAssetActual: useCallback((asset, value) => {
            dispatch({ type: SMSF_ACTIONS.SET_ASSET_ALLOCATION_ACTUAL, payload: { asset, value } });
        }, [dispatch]),
        
        setAssetValue: useCallback((asset, value) => {
            dispatch({ type: SMSF_ACTIONS.SET_ASSET_VALUE, payload: { asset, value } });
        }, [dispatch]),
        
        setAssetPerformance: useCallback((asset, value) => {
            dispatch({ type: SMSF_ACTIONS.SET_ASSET_PERFORMANCE, payload: { asset, value } });
        }, [dispatch]),
        
        // Beneficiaries
        setBeneficiaries: useCallback((beneficiaries) => {
            dispatch({ type: SMSF_ACTIONS.SET_BENEFICIARIES, payload: beneficiaries });
        }, [dispatch]),
        
        addBeneficiary: useCallback((beneficiary) => {
            dispatch({ type: SMSF_ACTIONS.ADD_BENEFICIARY, payload: beneficiary });
        }, [dispatch]),
        
        removeBeneficiary: useCallback((index) => {
            dispatch({ type: SMSF_ACTIONS.REMOVE_BENEFICIARY, payload: index });
        }, [dispatch]),
        
        updateBeneficiary: useCallback((index, data) => {
            dispatch({ type: SMSF_ACTIONS.UPDATE_BENEFICIARY, payload: { index, data } });
        }, [dispatch]),
        
        // UI
        setActiveTab: useCallback((tab) => {
            dispatch({ type: SMSF_ACTIONS.SET_ACTIVE_TAB, payload: tab });
        }, [dispatch]),
        
        toggleTutorial: useCallback(() => {
            dispatch({ type: SMSF_ACTIONS.TOGGLE_TUTORIAL });
        }, [dispatch]),
        
        // Bulk operations
        resetState: useCallback(() => {
            dispatch({ type: SMSF_ACTIONS.RESET_STATE });
        }, [dispatch]),
        
        loadState: useCallback((state) => {
            dispatch({ type: SMSF_ACTIONS.LOAD_STATE, payload: state });
        }, [dispatch]),
        
        mergeState: useCallback((state) => {
            dispatch({ type: SMSF_ACTIONS.MERGE_STATE, payload: state });
        }, [dispatch])
    };
};

// Selectors for derived state
export const useSMSFSelectors = () => {
    const state = useSMSFState();
    
    return {
        // Total fees
        getTotalFees: useCallback(() => {
            return Object.values(state.fees).reduce((sum, fee) => sum + fee, 0);
        }, [state.fees]),
        
        // Total contributions
        getTotalContributions: useCallback(() => {
            return state.contributions.concessionalUsed + state.contributions.nonConcessionalUsed;
        }, [state.contributions]),
        
        // Total TBC used
        getTotalTBCUsed: useCallback(() => {
            return state.contributions.yourTbcUsed + state.contributions.partnerTbcUsed;
        }, [state.contributions]),
        
        // Property LVR
        getPropertyLVR: useCallback(() => {
            if (state.property.propertyValue === 0) return 0;
            return (state.property.propertyLoan / state.property.propertyValue) * 100;
        }, [state.property]),
        
        // In-house asset ratio
        getInHouseAssetRatio: useCallback(() => {
            if (state.smsfData.balance === 0) return 0;
            return (state.property.inHouseAssets / state.smsfData.balance) * 100;
        }, [state.property.inHouseAssets, state.smsfData.balance]),
        
        // Total asset allocation
        getTotalAllocation: useCallback(() => {
            return Object.values(state.assetAllocation.actual).reduce((sum, val) => sum + val, 0);
        }, [state.assetAllocation.actual]),
        
        // Total beneficiary percentage
        getTotalBeneficiaryPercentage: useCallback(() => {
            return state.succession.beneficiaries.reduce((sum, ben) => sum + (ben.percentage || 0), 0);
        }, [state.succession.beneficiaries])
    };
};

export default SMSFStateProvider;
